/// <amd-module name="WIT/ProcessTemplateSelector/LinkCallout" />
import * as React from "react";
import { Button } from "VSSUI/Button";
import { Callout } from "VSSUI/Callout";
import { Link } from "VSSUI/Link";
import { IOrigin, Location } from "VSSUI/Utilities/Position";
import { getId } from "VSS/Core/Util/Object";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import * as Resources from "WIT/ProcessTemplateSelector/Resources";

export interface ILinkCalloutProps {
    /**
     * The link text clicking on which the callout will be displayed
     */
    linkText: string;
    /**
     * The main text that is displayed in the link callout
     */
    calloutText: string;
    /**
     * The tooltip to be shown on the link callout
     */
    tooltip?: string;
    /**
     * The aria-label to be shown on the link callout
     */
    ariaLabel?: string;
    /**
     * The tooltip for the main text in the callout, if not provided no tooltip will be set
     */
    calloutTextToolTip?: string;
    /**
     * The title for the link callout, rendered if the title is provided, else no title is rendered
     */
    calloutTitle?: string;
    /**
     * The tooltip for the title of the callout, if not provided no tooltip will be set
     */
    calloutTitleToolTip?: string;
    /**
     * Positioning origin of the callout against link text, default { horizontal: Location.end, vertical: Location.center }
     */
    calloutOrigin?: IOrigin;
    /**
     * The url to which the "Learn more" link would point to. If url is provided, the learn more link is rendered, else no learn more link is rendered
     */
    learnMoreUrl?: string;
    /**
     * Positioning origin of the link text against the callout, default { horizontal: Location.start, vertical: Location.center }
     */
    linkOrigin?: IOrigin;
    /**
     * The gap space between the target element and the callout, default is 20px
     */
    gapSpace?: number;
}

export interface ILinkCalloutState {
    isCalloutVisible?: boolean;
}

export class LinkCallout extends VssComponent<ILinkCalloutProps, ILinkCalloutState> {
    private _element: HTMLElement | null;
    private _id: string;
    private _calloutId: string;
    private _calloutTextId: string;
    private _calloutTitleId: string;

    constructor(props: ILinkCalloutProps, context: IVssComponentContext) {
        super(props, context);

        // State initialization
        this.state = {
            isCalloutVisible: false
        };
        this._id = getId("link-callout");
        this._calloutId = this._id + "-callout";
        this._calloutTextId = this._calloutId + "-text";
        this._calloutTitleId = this._calloutId + "-title";
    }

    public render(): JSX.Element {
        const {
            linkText,
            linkOrigin = { horizontal: Location.start, vertical: Location.center },
            calloutText,
            tooltip,
            ariaLabel,
            calloutOrigin = { horizontal: Location.end, vertical: Location.center },
            calloutTextToolTip,
            calloutTitle,
            calloutTitleToolTip,
            learnMoreUrl,
            gapSpace,
        } = this.props;

        return (
            <div className="link-callout"
                ref={this._saveElement}
                title={tooltip}
                aria-label={ariaLabel}>
                <Link
                    className={"link-callout-container"}
                    aria-owns={(this.state.isCalloutVisible ? this._calloutId : undefined)}
                    onClick={this._onIconClick}>
                    <span className="text">
                        {linkText}
                    </span>
                </Link>
                {
                    this.state.isCalloutVisible &&
                    <Callout
                        aria-labelledBy={calloutTitle && this._calloutTitleId}
                        aria-describedBy={calloutText && this._calloutTextId}
                        anchorElement={this._element!}
                        anchorOrigin={linkOrigin}
                        anchorOffset={{ horizontal: -5, vertical: 0 }}
                        blurDismiss={true}
                        calloutOrigin={calloutOrigin}
                        contentClassName="link-callout-dialog"
                        contentShadow={true}
                        focuszoneProps={{ focusOnMount: true, includeDefaults: true }}
                        onDismiss={this._onCalloutDismiss}
                        role="dialog"
                    >
                        <Button
                            subtle={true}
                            className="close-callout-button"
                            iconProps={{ iconName: "Cancel" }}
                            ariaLabel={Resources.CloseDialogButtonLabel}
                            onClick={this._onCalloutDismiss}
                        />
                        <div className="callout-details" id={this._calloutId} tabIndex={-1}>
                            {
                                calloutTitle &&
                                <div id={this._calloutTitleId} className="title" title={calloutTitleToolTip || ""} aria-label={calloutTitleToolTip || ""}>
                                    {calloutTitle}
                                </div>
                            }
                            <div id={this._calloutTextId} className="text" title={calloutTextToolTip || ""} aria-label={calloutTextToolTip || ""}>
                                {calloutText}
                            </div>
                            {
                                learnMoreUrl &&
                                <Link className="learn-more-link"
                                    href={learnMoreUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={Resources.LearnMoreText}>
                                    {Resources.LearnMoreText}
                                </Link>
                            }
                        </div>
                    </Callout>
                }
            </div>
        );
    }

    private _saveElement = (icon: HTMLDivElement | null) => {
        this._element = icon
    };

    /**
     * Onclick event handler for both the link and close icons. It toggles the visibility of the callout on click
     */
    private _onIconClick = (): void => {
        this.setState({
            isCalloutVisible: !this.state.isCalloutVisible
        });
    }

    /**
     * Close the callout when the callout is dismissed by clicking else where in the view
     */
    private _onCalloutDismiss = (): void => {
        this.setState({
            isCalloutVisible: false
        });
    }
}
